import React, { Fragment, useState, useEffect, useCallback, useMemo } from 'react';

// components
import UserFormModal from './components/UserFormModal';
import ModalAlert from '../../../components/Modals/ModalAlert';
import Table from '../../../components/Table/Table';
import SquareDelete from '../../../components/SquareDelete/SquareDelete';
import SquareEdit from '../../../components/SquareEdit/SquareEdit';
import Button from '../../../components/Buttons/Button';
import { Search } from '../../../utils/Search';
import styles from '../../../styles/table.module.scss';

// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectUserList } from '../../../redux/selectors/user';
import { selectCurrentPartner } from '../../../redux/selectors/authentication';
import { doDeleteUser, doReadUserList } from '../../../redux/thunks/user';
import { doUserListReset } from '../../../redux/actionCreators/user';
import { doReadRoleListSuccess, doRoleListReset } from '../../../redux/actionCreators/role';
import axios from 'axios';
import useIsMobile from '../../../hooks/useIsMobile';
import USER_TYPES from '../../../constants/types/UserTypes';
import MobileTable from '../../../components/MobileTable/MobileTable';
import { Link } from '@material-ui/core';
import SDialog from '../../../components/SDialog/SDialog';
import Typography from '../../../components/Typography/Typography';
import FlexRow from '../../../components/FlexBox/FlexRow';
import SButton from '../../../components/SButton/SButton';
import { axiosInstance } from '../../../services/api';

const UserList = () => {

  const isMobile = useIsMobile();
  const dispatch = useDispatch();

  const { currentPartner, userList } = useSelector((state) => ({
    currentPartner: selectCurrentPartner(state),
    userList: selectUserList(state),
  }));


  // eslint-disable-next-line no-unused-vars
  const [popupTitle, setPopupTitle] = useState('Add');
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [user, setUser] = useState(null);

  const [filters, setFilters] = useState({ name: '', id: '', email: '', phoneNumber: '' });
  const [pagination, setPagination] = useState({ total: 0, currentPage: 1, perPage: 25 });

  useEffect(() => {
    setPagination(prev => ({
      ...prev,
      total: userList?.users.length ? userList?.users.length : 0
    }));
  }, [setPagination, userList?.users.length]);

  const handleDeleteUser = useCallback(() => {
    dispatch(doDeleteUser({ partnerId: currentPartner?.partnerId, userId: user?.id }));
    setUser(null);
    setShowDeleteUser(false);
  }, [currentPartner?.partnerId, dispatch, user?.id]);

  const columns = useMemo(() => {
    const commonColumns = [
      { title: 'Id', field: 'id', name: 'id' },
      {
        title: 'Name',
        field: 'name',
        name: 'name',
        render: (_, rowData) => (
          <div className={styles['user_details']}>
            <div className={styles['avatar']}>
              <p>{rowData?.name?.split('')[0]}</p>
            </div>
            <Link className={styles['cell-link']}>{rowData?.name || ''}</Link>
          </div>
        ),
        renderM: (_, rowData) => (
          <Link className={styles['cell-link']}>{rowData?.name || ''}</Link>
        ),
      },
      { title: 'Contact Number', field: 'phoneNumber', name: 'phoneNumber' },
      {
        title: 'Email Id',
        field: 'email',
        name: 'email',
      },
      {
        title: 'User Role',
        field: 'role.name',
        // name: 'name',
        render: (columnProps, rowData) => {
          const user_type = USER_TYPES[rowData.role.name];
          return <p>{user_type}</p>;
        },
      },

    ]

    const actions = [
      {
        title: 'Action',
        render: (_, rowData, row) => {
          return (
            <FlexRow justifyContent='end' gap='0.5rem'>
              <SquareEdit
                color={'#ff535a'}
                style={{
                  height: '2rem',
                  width: '2rem'
                }}
                onPress={() => {
                  setPopupTitle('Edit');
                  setUser(rowData);
                  togglePopup('AddOrEditUser');
                }}
              />
              <SquareDelete
                style={{
                  height: '2rem',
                  width: '2rem',
                  padding: '0'
                }}
                color={'#ff535a'}
                onPress={() => {
                  setPopupTitle('Delete');
                  setUser(rowData);
                  setShowDeleteUser(true);
                }}
              />
            </FlexRow>
          );
        },
        renderM: (_, rowData, row) => {
          return (
            <FlexRow justifyContent='end' gap='0.5rem'>
              <SquareEdit
                color={'#ff535a'}
                style={{
                  height: '2rem',
                  width: '2rem'
                }}
                onPress={() => {
                  setPopupTitle('Edit');
                  setUser(rowData);
                  togglePopup('AddOrEditUser');
                }}
              />
              <SquareDelete
                style={{
                  height: '2rem',
                  width: '2rem',
                  padding: '0'
                }}
                color={'#ff535a'}
                onPress={() => {
                  setPopupTitle('Delete');
                  setUser(rowData);
                  setShowDeleteUser(true);
                }}
              />
            </FlexRow>
          );
        },
      },
    ];

    return {
      allColumns: [...commonColumns, ...actions],
      commonColumns,
      actions
    }

  }, []);

  const mobileFiltersConfig = useMemo(() => {
    return [
      { name: "Name", key: "name", type: "search" },
      { name: "ID", key: "id", type: "search" },
      { name: "Email ID", key: "email", type: "search" },
      { name: "Contact Number", key: "phoneNumber", type: "search" },
    ]
  }, [])

  useEffect(() => {
    const getRoles = async () => {
      try {
        const rolesURL = `/partnerManagement/${import.meta.env.VITE_APP_BASE_API_VER_V3}/partners/${currentPartner.partnerId}/roles`
        const res = await axiosInstance({ method: 'GET', url: rolesURL });
        dispatch(doReadRoleListSuccess(res.data));
      } catch (err) {
        console.err(err);
      }
    };
    if (currentPartner.partnerId) {
      getRoles();
    }
    return () => {
      dispatch(doRoleListReset());
    };
  }, [currentPartner.partnerId]);

  // setting up phoneNumber

  const togglePopup = (popupName) => {
    if (popupName === 'AddOrEditUser') {
      setShowUserModal(!showUserModal);
      if (showUserModal) {
        setUser(null);
      }
    }
    if (popupName === 'DeleteUser') {
      setShowDeleteUser(!showDeleteUser);
      if (showDeleteUser) {
        setUser(null);
      }
    }
  };

  useEffect(() => {
    if (currentPartner) dispatch(doReadUserList(currentPartner.partnerId));

    return () => {
      dispatch(doUserListReset());
    };
  }, [dispatch, currentPartner]);

  const filteredData = useCallback(() => {
    let data = userList?.users ? userList?.users : [];
    if (filters.id !== '') {
      data = Search(data, 'id', filters.id);
    }
    if (filters.name !== '') {
      data = Search(data, 'name', filters.name);
    }
    if (filters.phoneNumber !== '') {
      data = Search(data, 'phoneNumber', `+91${filters.phoneNumber}`);
    }
    if (filters.email !== '') {
      data = Search(data, 'email', filters.email);
    }

    if (!isMobile) {
      data = data.slice(
        (pagination.currentPage - 1) * 7,
        pagination.currentPage * pagination.perPage,
      );
    }

    return data;
  }, [
    filters.email,
    filters.id,
    filters.name,
    filters.phoneNumber,
    pagination.currentPage,
    pagination.perPage,
    userList?.users,
    isMobile
  ]);

  const hSearchChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const search = {
    onChange: hSearchChange,
  };

  const changePage = (p) => {
    const { page, per_page } = p;
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: per_page,
    }))
  };

  return (
    <Fragment>
      <div className="spacer__0-5" />
      <div>
        <div>
          {showUserModal && (
            <UserFormModal popupTitle={popupTitle} showUserModal={showUserModal} togglePopup={togglePopup} user={user} setUser={setUser} />
          )}
          {showDeleteUser && (
            <SDialog
              title='Delete User'
              open={showDeleteUser}
              onClose={() => {
                setShowDeleteUser(false);
              }}
              titleConfig={{
                variant: isMobile ? 'h6M' : 'h5M'
              }}
              secondaryButtonConfig={{
                text: 'Cancel',
                onClick: () => {
                  setShowDeleteUser(false);
                }
              }}
              primaryButtonConfig={{
                text: 'Delete',
                color: 'error',
                onClick: handleDeleteUser
              }}
            >
              <Typography variant={isMobile ? "b2M" : "b1M"} style={{ padding: '1.5rem 0' }}>Are you sure you want to delete {user ? user.name : ''} ? This action is non
                reversible and will cause you to lose all data associated with this user/ s</Typography>
            </SDialog>
          )}
          <FlexRow justifyContent='space-between' styles={{ marginBottom: '1rem' }}>
            <Typography variant={"h7M"}>Users</Typography>
            <SButton
              onClick={() => {
                setPopupTitle('Add');
                togglePopup('AddOrEditUser');
              }}
              minWidth
            >
              Add User
            </SButton>
          </FlexRow>

          {isMobile ?
            <MobileTable
              columns={columns?.commonColumns}
              data={filteredData()}
              actions={columns.actions}
              filtersConfig={mobileFiltersConfig}
              filters={filters}
              setFilters={setFilters}
              emptyMessage="No Records found"
            />
            :
            <Table
              columns={columns?.allColumns}
              data={filteredData()}
              pagination={pagination}
              options={{ search }}
              emptyContent="No Records found"
              changePage={changePage}
            />
          }
        </div>
      </div>
    </Fragment>
  );
};

export default UserList;
